import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  Button,
  Search,
  Checkbox,
  DatePicker,
  DatePickerInput,
  Slider,
  InlineNotification,
} from '@carbon/react';
import { useAtom, useAtomValue } from 'jotai';
import classNames from 'classnames';
import {
  TcWellFilters,
  FilterCategory,
  CategoricalFacet,
  RangeFacet,
  TcWellsFacets,
} from '../types';
import { TC_WELL_FILTER_CATEGORIES } from './utils';

import {
  tcWellFilterModalOpenAtom,
  tcWellFilterModalCategoryAtom,
  tcWellFiltersAtom,
  tcPendingWellFiltersAtom,
  tcWellsFacetsAtom,
  tcInventoryAreaFormationsAtom,
} from './atoms';
import { useWellFilterSync, parseFilterError } from './use-saved-well-filters';
import { format0 } from 'utils/number';
import { PasteListControl } from 'components/paste-list';
import { toDateParam, fromDateParam } from 'utils/dates';
import styles from './tc-well-filter-modal.module.scss';

interface FacetOption {
  id: string;
  name: string;
}

function getFacetPicklistOptions(
  category: FilterCategory,
  facets: TcWellsFacets | null
): FacetOption[] {
  if (!facets || !category.facetKey) return [];
  const facet = facets[category.facetKey] as CategoricalFacet | undefined;
  if (!facet?.buckets) return [];
  return facet.buckets
    .map((b) => ({ id: b.id, name: b.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function getFacetNumericRange(
  category: FilterCategory,
  facets: TcWellsFacets | null
): { min: number; max: number } | null {
  if (!facets || !category.facetKey) return null;
  const facet = facets[category.facetKey] as RangeFacet | undefined;
  if (
    !facet ||
    facet.min === null ||
    facet.min === undefined ||
    facet.max === null ||
    facet.max === undefined
  )
    return null;
  return {
    min: Math.floor(Number(facet.min)),
    max: Math.ceil(Number(facet.max)),
  };
}

const CategoryList = ({
  activeCategory,
  onSelect,
}: {
  activeCategory: string;
  onSelect: (id: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const categories = TC_WELL_FILTER_CATEGORIES.filter((cat) =>
    cat.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={styles.categoriesInner}>
      <div className={styles.categoriesSearch}>
        <Search
          labelText="Search filters"
          placeholder="Search Filter Names"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className={styles.categoryList}>
        {categories.map((cat) => (
          <li key={cat.id} className={styles.categoryItem}>
            <button
              type="button"
              onClick={() => onSelect(cat.id)}
              className={classNames(styles.categoryButton, {
                [styles.categoryButtonActive]: activeCategory === cat.id,
              })}
            >
              {cat.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Filters whose options are non-exhaustive, indicating to the frontend to allow
 * unmatched options when pasting them in.
 *
 * These hold roughly one value per well, and
 * `Query::Wells::Elastic::TC_FACET_TERMS_SIZE` caps their facets at 1,000
 * buckets, meaning a type curve with more wells than that will only show 1,000
 * values.
 */
const NON_EXHAUSTIVE_CATEGORY_IDS = new Set([
  'uwi',
  'wellName',
  'leaseName',
  'wellNumber',
]);

const PicklistFormContent = ({
  category,
  facets,
  selected,
  onChange,
}: {
  category: FilterCategory;
  facets: TcWellsFacets | null;
  selected: string[];
  onChange: (values: string[]) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Inventory Workspace: the Formation options come from the selected type curve
  // area's formations (the sidebar tree), not the server facets. `null` off the
  // Inventory Workspace, so plain Type Curve pages keep the facet options.
  const areaFormations = useAtomValue(tcInventoryAreaFormationsAtom);
  const options = useMemo(() => {
    if (areaFormations && category.id === 'formation') {
      return areaFormations.map((f) => ({
        id: f.formation,
        name: f.formation,
      }));
    }
    return getFacetPicklistOptions(category, facets);
  }, [category, facets, areaFormations]);
  const filteredOptions = useMemo(() => {
    return searchTerm
      ? options.filter((o) =>
          o.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;
  }, [options, searchTerm]);

  const filteredIds = useMemo(
    () => filteredOptions.map((o) => o.id),
    [filteredOptions]
  );

  const allSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));

  return (
    <>
      <Search
        labelText={`Search ${category.label}`}
        placeholder={`Search ${category.label.toLowerCase()}...`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="sm"
      />
      <PasteListControl
        id={`tc-filter-${category.id}`}
        label={category.label}
        options={options}
        selectedIds={selected}
        onApply={onChange}
        acceptUnmatched={NON_EXHAUSTIVE_CATEGORY_IDS.has(category.id)}
      />
      <div className={styles.selectRow}>
        <button
          type="button"
          className={styles.selectAllLink}
          onClick={() => {
            if (allSelected) {
              onChange(selected.filter((v) => !filteredIds.includes(v)));
            } else {
              onChange([...new Set([...selected, ...filteredIds])]);
            }
          }}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
        <span className={styles.resultCount}>
          {filteredOptions.length} results
        </span>
      </div>
      <div className={styles.fieldStack}>
        {filteredOptions.map((option) => (
          <Checkbox
            key={option.id}
            id={`tc-filter-${category.id}-${option.id}`}
            labelText={option.name}
            checked={selected.includes(option.id)}
            onChange={(_evt, { checked }) => {
              onChange(
                checked
                  ? [...selected, option.id]
                  : selected.filter((v) => v !== option.id)
              );
            }}
          />
        ))}
        {filteredOptions.length === 0 && (
          <p className={styles.noResults}>No options available</p>
        )}
      </div>
    </>
  );
};

const DateRangeFormContent = ({
  category,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
}: {
  category: FilterCategory;
  minValue?: string;
  maxValue?: string;
  onMinChange: (value: string | undefined) => void;
  onMaxChange: (value: string | undefined) => void;
}) => (
  <div className={styles.rangeRow}>
    <div className={styles.rangeInputs}>
      <DatePicker
        datePickerType="single"
        value={fromDateParam(minValue)}
        onChange={([date]: Date[]) =>
          onMinChange(date ? toDateParam(date) : undefined)
        }
      >
        <DatePickerInput
          id={`${category.id}-min`}
          labelText="From"
          placeholder="mm/dd/yyyy"
        />
      </DatePicker>
      <DatePicker
        datePickerType="single"
        value={fromDateParam(maxValue)}
        onChange={([date]: Date[]) =>
          onMaxChange(date ? toDateParam(date) : undefined)
        }
      >
        <DatePickerInput
          id={`${category.id}-max`}
          labelText="To"
          placeholder="mm/dd/yyyy"
        />
      </DatePicker>
    </div>
  </div>
);

function computeSliderStep(min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 1;
  if (range <= 10) return 0.1;
  if (range <= 100) return 1;
  if (range <= 1000) return 10;
  if (range <= 10000) return 100;
  return 1000;
}

const NumericRangeFormContent = ({
  category,
  facets,
  minValue,
  maxValue,
  onChange,
}: {
  category: FilterCategory;
  facets: TcWellsFacets | null;
  minValue?: number;
  maxValue?: number;
  onChange: (min: number | undefined, max: number | undefined) => void;
}) => {
  const range = useMemo(
    () => getFacetNumericRange(category, facets),
    [category, facets]
  );

  const rangeMin = range?.min ?? 0;
  const rangeMax = range?.max ?? 100;
  const step = computeSliderStep(rangeMin, rangeMax);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  return (
    <div className={styles.sliderGroup}>
      <Slider
        id={`${category.id}-range`}
        labelText={category.label}
        hideLabel
        ariaLabelInput="Min"
        unstable_ariaLabelInputUpper="Max"
        min={rangeMin}
        max={rangeMax}
        value={minValue ?? rangeMin}
        unstable_valueUpper={maxValue ?? rangeMax}
        step={step}
        stepMultiplier={Math.max(1, Math.round(10 / step))}
        formatLabel={(v: number) => format0(v)}
        onChange={({
          value,
          valueUpper,
        }: {
          value: number;
          valueUpper?: number;
        }) => {
          onChangeRef.current(value, valueUpper);
        }}
        onRelease={({
          value,
          valueUpper,
        }: {
          value: number;
          valueUpper?: number;
        }) => {
          onChangeRef.current(value, valueUpper);
        }}
      />
    </div>
  );
};

const FilterForm = ({
  activeCategory,
  pending,
  onChange,
  facets,
}: {
  activeCategory: string;
  pending: TcWellFilters;
  onChange: (filters: TcWellFilters) => void;
  facets: TcWellsFacets | null;
}) => {
  const category = TC_WELL_FILTER_CATEGORIES.find(
    (c) => c.id === activeCategory
  );
  if (!category) return null;

  const hasCategoryFilter = (() => {
    if (category.type === 'picklist') {
      const vals = pending[category.param];
      return Array.isArray(vals) && vals.length > 0;
    }
    const hasMin =
      category.minParam && pending[category.minParam] !== undefined;
    const hasMax =
      category.maxParam && pending[category.maxParam] !== undefined;
    return !!(hasMin || hasMax);
  })();

  const selectedPills: string[] = (() => {
    if (category.type === 'picklist') {
      return (pending[category.param] as string[] | undefined) ?? [];
    }
    if (category.type === 'date-range') {
      const min = pending[category.minParam!] as string | undefined;
      const max = pending[category.maxParam!] as string | undefined;
      if (!min && !max) return [];
      const fMin = min ? fromDateParam(min) : '';
      const fMax = max ? fromDateParam(max) : '';
      if (fMin && fMax) return [`${fMin} – ${fMax}`];
      if (fMin) return [`From ${fMin}`];
      if (fMax) return [`To ${fMax}`];
      return [];
    }
    if (category.type === 'numeric-range') {
      const min = pending[category.minParam!] as number | undefined;
      const max = pending[category.maxParam!] as number | undefined;
      if (min === undefined && max === undefined) return [];
      if (min !== undefined && max !== undefined)
        return [`${format0(min)} – ${format0(max)}`];
      if (min !== undefined) return [`Min: ${format0(min)}`];
      return [`Max: ${format0(max)}`];
    }
    return [];
  })();

  const handleClear = () => {
    const updated = { ...pending };
    if (category.type === 'picklist') {
      updated[category.param] = [];
    } else {
      if (category.minParam) delete updated[category.minParam];
      if (category.maxParam) delete updated[category.maxParam];
    }
    onChange(updated);
  };

  let content: React.ReactNode = null;

  if (category.type === 'picklist') {
    const selected = (pending[category.param] as string[] | undefined) ?? [];
    content = (
      <PicklistFormContent
        key={category.id}
        category={category}
        facets={facets}
        selected={selected}
        onChange={(values) =>
          onChange({ ...pending, [category.param]: values })
        }
      />
    );
  } else if (category.type === 'date-range') {
    content = (
      <DateRangeFormContent
        category={category}
        minValue={pending[category.minParam!] as string | undefined}
        maxValue={pending[category.maxParam!] as string | undefined}
        onMinChange={(v) => onChange({ ...pending, [category.minParam!]: v })}
        onMaxChange={(v) => onChange({ ...pending, [category.maxParam!]: v })}
      />
    );
  } else if (category.type === 'numeric-range') {
    content = (
      <NumericRangeFormContent
        category={category}
        facets={facets}
        minValue={pending[category.minParam!] as number | undefined}
        maxValue={pending[category.maxParam!] as number | undefined}
        onChange={(min, max) =>
          onChange({
            ...pending,
            [category.minParam!]: min,
            [category.maxParam!]: max,
          })
        }
      />
    );
  }

  return (
    <div className={styles.formWrapper}>
      <h4 className={styles.formTitle}>{category.label}</h4>
      <div className={styles.selectedFilters}>
        <span className={styles.selectedFiltersLabel}>Selected Filters</span>
        {selectedPills.length === 0 ? (
          <span className={styles.selectedFiltersEmpty}>
            No filters selected
          </span>
        ) : (
          <div className={styles.selectedFiltersPills}>
            {selectedPills.map((pill) => (
              <span key={pill} className={styles.selectedFiltersPill}>
                {pill}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={styles.formContent}>{content}</div>
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.clearLink}
          onClick={handleClear}
          disabled={!hasCategoryFilter}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export const TcWellFilterModal = () => {
  const [isOpen, setIsOpen] = useAtom(tcWellFilterModalOpenAtom);
  const targetCategory = useAtomValue(tcWellFilterModalCategoryAtom);
  const [appliedFilters, setAppliedFilters] = useAtom(tcWellFiltersAtom);
  const [pending, setPending] = useAtom(tcPendingWellFiltersAtom);
  const facets = useAtomValue(tcWellsFacetsAtom);
  const [activeCategory, setActiveCategory] = useState<string>(
    TC_WELL_FILTER_CATEGORIES[0].id
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { syncFilters, isSaving } = useWellFilterSync();

  useEffect(() => {
    if (!isOpen) return;
    // Seed the modal's working copy from the currently applied filters, and open
    // on the category the caller asked for (an applied filter's edit icon) —
    // otherwise the first one, for the plain "Add Filter" path.
    setActiveCategory(targetCategory ?? TC_WELL_FILTER_CATEGORIES[0].id);
    setErrorMsg(null);
    setPending({ ...appliedFilters });
    // Only re-seed when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleApply = async () => {
    setErrorMsg(null);
    try {
      // Persist per-property changes (POST/PATCH/DELETE), wait for them, then
      // commit to the analysis atom so UWIs refetch against saved state.
      await syncFilters(pending, appliedFilters);
      setAppliedFilters({ ...pending });
      setIsOpen(false);
    } catch (err) {
      setErrorMsg(
        (await parseFilterError(err)) ?? 'Could not save filters. Try again.'
      );
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleReset = () => {
    setPending({});
  };

  return (
    <ComposedModal
      open={isOpen}
      onClose={handleCancel}
      size="lg"
      className={styles.modal}
    >
      <ModalHeader title="Wells Filters" />
      <ModalBody>
        {errorMsg ? (
          <InlineNotification
            kind="error"
            lowContrast
            title="Save failed"
            subtitle={errorMsg}
            onCloseButtonClick={() => setErrorMsg(null)}
          />
        ) : null}
        <div className={styles.content}>
          <div className={styles.categories}>
            <CategoryList
              activeCategory={activeCategory}
              onSelect={setActiveCategory}
            />
          </div>
          <div className={styles.form}>
            <FilterForm
              activeCategory={activeCategory}
              pending={pending}
              onChange={setPending}
              facets={facets}
            />
          </div>
        </div>
      </ModalBody>
      <div className={styles.customFooter}>
        <Button
          size="lg"
          kind="ghost"
          className={styles.footerButton}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          kind="secondary"
          className={styles.footerButton}
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          size="lg"
          kind="primary"
          className={styles.footerButton}
          onClick={handleApply}
          disabled={isSaving}
        >
          {isSaving ? 'Applying…' : 'Apply Filters'}
        </Button>
      </div>
    </ComposedModal>
  );
};
